import { queryOptions } from '@tanstack/react-query';
import { AnalyticsService } from '@inference.engine/api-client/web';

import { inferenceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

import type { AnalyticsMetricKey, AnalyticsRange, AnalyticsSeries } from '../model/types';
import { ANALYTICS_QUERY_KEYS } from './query-keys';

// The backend caches each series for ~30 minutes, and the upstream data lags by
// a couple of hours, so refetching aggressively buys nothing.
const STALE_TIME_MS = 5 * 60 * 1000;

/*
 * Every fetcher below suppresses the global problem-details toast, because each
 * card renders its own inline "unavailable" state. The toast would duplicate it
 * loudly: it is persistent and not deduplicated, and the dashboard fires seven
 * queries at once. Both degraded states this feature supports are
 * all-or-nothing (no Amplitude credentials, so the routes 404; or a non-admin,
 * so every route 403s), which would otherwise queue seven non-dismissing toasts
 * reading things like "Cannot GET /api/v1/analytics/active-users".
 */

const getActiveUsers = async (range: AnalyticsRange): Promise<AnalyticsSeries> => {
  const response = await AnalyticsService.getActiveUsers({
    client: inferenceHttp,
    throwOnError: true,
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
    query: { range },
  });
  return response.data;
};

const getSessionDuration = async (range: AnalyticsRange): Promise<AnalyticsSeries> => {
  const response = await AnalyticsService.getSessionDuration({
    client: inferenceHttp,
    throwOnError: true,
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
    query: { range },
  });
  return response.data;
};

const getMetric = async (
  metricKey: AnalyticsMetricKey,
  range: AnalyticsRange,
): Promise<AnalyticsSeries> => {
  const response = await AnalyticsService.getEventMetric({
    client: inferenceHttp,
    throwOnError: true,
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
    path: { metricKey },
    query: { range },
  });
  return response.data;
};

/**
 * Every query is disabled until a workspace is known. The workspace itself is
 * never sent explicitly - the request interceptor attaches
 * `x-selected-workspace-id` - but it belongs in the key so the cache is scoped.
 */
const baseOptions = (workspaceId: string | null | undefined) => ({
  enabled: Boolean(workspaceId),
  staleTime: STALE_TIME_MS,
  refetchOnWindowFocus: false,
});

export const activeUsersQuery = (workspaceId: string | null | undefined, range: AnalyticsRange) =>
  queryOptions({
    queryKey: ANALYTICS_QUERY_KEYS.activeUsers(workspaceId ?? '', range),
    queryFn: () => getActiveUsers(range),
    ...baseOptions(workspaceId),
  });

export const sessionDurationQuery = (
  workspaceId: string | null | undefined,
  range: AnalyticsRange,
) =>
  queryOptions({
    queryKey: ANALYTICS_QUERY_KEYS.sessionDuration(workspaceId ?? '', range),
    queryFn: () => getSessionDuration(range),
    ...baseOptions(workspaceId),
  });

export const analyticsMetricQuery = (
  workspaceId: string | null | undefined,
  metricKey: AnalyticsMetricKey,
  range: AnalyticsRange,
) =>
  queryOptions({
    queryKey: ANALYTICS_QUERY_KEYS.metric(workspaceId ?? '', metricKey, range),
    queryFn: () => getMetric(metricKey, range),
    ...baseOptions(workspaceId),
  });
